<script>
  import {tick} from 'svelte';
  import _ from 'lodash';
  import {fade} from 'svelte/transition';
  import {myChannels, selectChannel} from './channelStore';
  import ChannelForm from './ChannelForm.svelte';

  import ChannelListItem from "./ChannelListItem.svelte";
  import Loading from "../general/Loading.svelte";
  import VirtualList from '@sveltejs/svelte-virtual-list';
  import ChannelListProfile from "../users/ChannelListProfile.svelte";
  import {ChannelGroup, ChannelGroupHeader} from "./channelModels";
  import ChannelGroupListItem from "./ChannelGroupListItem.svelte";
  import {hideModal, showModal} from "../modal/modalStore";
  import Emoji from "./Emoji.svelte";

  export let darkTheme = false;
  export let style = '';

  let selectedChannelGroup = null;

  const groupChannels = channels => {
    const orderedChannels = _.orderBy(channels, c => c.data().name);

    const archivedChannels = [];
    const favoriteChannels = [];
    const normalChannels = [];

    _.forEach(orderedChannels, c => {
      const channelData = c.data();
      if (channelData.archived) {
        archivedChannels.push(c);
      } else if (channelData.isFavorite) {
        favoriteChannels.push(c);
      } else {
        normalChannels.push(c);
      }
    });

    const groupedChannels = [
      new ChannelGroupHeader({
        name: 'Favorite',
        icon: 'star-outline'
      }),
      ...favoriteChannels,
      new ChannelGroupHeader({
        name: 'Channels',
        icon: 'cube-outline',
        actionIcon: 'add-circle-outline',
        action: () => {
          const modalId = 'create-channel-form-modal-' + Date.now();
          showModal(ChannelForm, {
            id: modalId,
            modalProps: {
              width: 400,
              height: 'auto'
            },
            contentProps: {
              onSubmit: () => hideModal(modalId)
            }
          });
        }
      }),
      ...normalChannels
    ];

    if (!_.isEmpty(archivedChannels)) {
      groupedChannels.push(new ChannelGroup({
        icon: 'archive-outline',
        name: `Archived (${_.size(archivedChannels)})`,
        channels: archivedChannels
      }));
    }

    return groupedChannels;
  };


  const searchLimit = 6;
  let lastShiftPressed = 0;
  let searchBarVisible = false;
  let searchInputElement = null;
  let searchInputValue = '';
  let selectedSearchItemIndex = 0;

  const onKeyDown = e => {

    if (e.key === 'Shift' && !searchBarVisible) {
      const now = Date.now();

      if (now - lastShiftPressed < 300) {
        searchBarVisible = true;
        selectedSearchItemIndex = 0;
        tick().then(() => searchInputElement && searchInputElement.focus());
      }

      lastShiftPressed = now;
    } else if (e.key === 'Escape' && searchBarVisible) {
      hideQuickSearch();
    }

  };

  const onSearchInputKeyDown = e => {
    if (e.key === 'Enter') {
      const targetChannel = getFilteredChannels($myChannels, searchInputValue, searchLimit)[selectedSearchItemIndex];
      if (!targetChannel) return;
      selectChannel(targetChannel);
      hideQuickSearch();
    } else if (e.key === 'ArrowUp') {
      selectedSearchItemIndex = Math.max(selectedSearchItemIndex - 1, 0);
    } else if (e.key === 'ArrowDown') {
      selectedSearchItemIndex = Math.min(selectedSearchItemIndex + 1, _.size(getFilteredChannels($myChannels, searchInputValue, searchLimit)));
    }
  };

  const onMouseDown = e => searchBarVisible && hideQuickSearch();

  const getFilteredChannels = (channels, keyword, limit = 10) => {
    if (!keyword) return [];

    return _(channels).filter(c => c.data().name.toLowerCase().includes(keyword.toLowerCase())).orderBy(c => c.data().name).take(limit).value();
  };

  const hideQuickSearch = () => {
    searchBarVisible = false;
    searchInputValue = '';
  };

</script>

<svelte:window on:keydown={onKeyDown} on:mousedown={onMouseDown}></svelte:window>

<column style={style} class:dark={darkTheme}>
  <ChannelListProfile darkTheme={darkTheme}/>

  <row expand class="page-container" class:next-page={!!selectedChannelGroup}>
    <div class="page">
        {#if !$myChannels}
          <Loading fullscreen/>
        {:else}
          <VirtualList items={groupChannels($myChannels)} let:item>
              {#if item instanceof ChannelGroupHeader}
                <row class="channel-group-header" align-center>
                  <ion-icon name={item.icon}></ion-icon>
                  <expand class="channel-group-name">
                      {item.name}
                  </expand>
                    {#if item.action && item.actionIcon}
                      <button class="clickable button-action" on:click={item.action}>
                        <ion-icon name={item.actionIcon}></ion-icon>
                      </button>
                    {/if}
                </row>
              {:else if item instanceof ChannelGroup}
                <div class="clickable" on:click={() => selectedChannelGroup = item}>
                  <ChannelGroupListItem channelGroup={item} darkTheme={darkTheme}/>
                </div>
              {:else}
                <ChannelListItem channel={item} darkTheme={darkTheme}/>
              {/if}
          </VirtualList>
        {/if}
    </div>

      {#if selectedChannelGroup}
        <div class="page" transition:fade>
          <row align-center class="selected-channel-group-header-wrap">
            <button class="clickable button-back" on:click={() => selectedChannelGroup = null}>
              <ion-icon name="chevron-back-outline"></ion-icon>
            </button>
            <div class="selected-channel-group-header">
                {selectedChannelGroup.name}
            </div>
          </row>
          <VirtualList items={selectedChannelGroup.channels} let:item>
            <ChannelListItem channel={item} darkTheme={darkTheme}/>
          </VirtualList>
        </div>
      {/if}
  </row>
</column>

{#if searchBarVisible}
  <div class="quick-search-container">
    <column class="quick-search">
      <row align-center class="searchbar">
        <ion-icon name="search-outline"></ion-icon>
        <input
            on:keydown={onSearchInputKeyDown}
            type="text" class="expand" bind:this={searchInputElement} bind:value={searchInputValue} placeholder="Search channels">
      </row>
      <div class="filtered-channel-list">
          {#each getFilteredChannels($myChannels, searchInputValue, searchLimit) as channel, index}
            <row align-center class="channel-list-item clickable {selectedSearchItemIndex === index ? 'selected' : ''}" on:click={e => selectChannel(channel)}>
              <div class="emoji">
                  {#if channel.data().archived}
                    <ion-icon name="archive-outline"></ion-icon>
                  {:else}
                    <Emoji emoji={channel.data().emoji}></Emoji>
                  {/if}
              </div>
              <div class="channel-name expand">{channel.data().name}</div>

                {#if index === selectedSearchItemIndex}
                  <ion-icon class="icon-enter" name="return-down-back-outline"></ion-icon>
                {/if}
            </row>
          {/each}
      </div>
    </column>
  </div>
{/if}

<style>
  .channel-group-header, .selected-channel-group-header {
    color: #888;
    font-size: .8rem;
    padding: 8px 8px 4px;
  }

  .channel-group-header ion-icon {
    margin: 0 4px;
  }

  .selected-channel-group-header-wrap {
    height: 40px;
  }

  .dark .channel-group-header, .dark .selected-channel-group-header {
    color: #ccc;
  }

  .button-action {
    width: 32px;
    height: 32px;
    background: none;
    border: none;
  }

  .button-action ion-icon {
    font-size: 20px;
    color: black;
  }

  .dark .button-action ion-icon {
    color: white;
  }


  .page-container {
    transition: all .2s;
  }

  .page-container.next-page {
    transform: translate(-100%, 0);
  }

  .page {
    width: 100%;
    height: 100%;
  }

  .button-back {
    font-size: 20px;
    background: none;
    border: none;
    width: 32px;
    color: #888;
  }

  .dark .button-back {
    color: #ccc;
  }

  .button-back ion-icon {
    vertical-align: middle;
  }

  .selected-channel-group-header {
    font-size: 1rem;
  }

  .quick-search-container {
    position: fixed;
    background-color: white;
    top: 10%;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 8px;
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.3);
    width: 600px;
    max-width: 80%;
  }

  .searchbar {
    height: 60px;
  }

  .searchbar ion-icon {
    font-size: 30px;
    color: #ccc;
    margin: 0 12px;
  }

  .searchbar input {
    height: 32px;
    border: none;
    font-size: 1.2rem;
    margin-right: 12px;
  }

  .channel-list-item {
    height: 40px;
    font-size: 1.2rem;
    padding-right: 8px;
  }

  .channel-list-item.selected {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .channel-list-item:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }

  .channel-list-item .emoji {
    font-size: 30px;
    margin: 0 12px;
  }

  .channel-list-item .icon-enter {
    font-size: 20px;
    color: #ccc;
    margin-right: 12px;
  }
</style>
